import { html, property } from 'lit-element';
import { nothing } from 'lit-html';
import {
  event,
  EventEmitter,
  KatLitElement,
  register,
} from '../../shared/base';
import baseStyles from '../../shared/base/base.lit.scss';
import styles from './kpi-bar-item.lit.scss';

/**
 * @component {kat-kpi-bar-item} KatKpiBarItem A simple trend chart
 * @status Production
 * @theme flo
 * @slot default Should be filled with visualization
 * @slot error Error message
 * @example Default {"label": "FBA Sales", "metric": "$1,234.00"}
 */
@register('kat-kpi-bar-item')
export class KatKpiBarItem extends KatLitElement {
  static get styles() {
    return [baseStyles, styles];
  }

  /** Title text that will be displayed to the user. */
  @property()
  label: string;

  /** The primary metric value to be displayed. */
  @property()
  metric: string;

  /**
   * The trend of a metric being displayed. Intended to be used with the `metric` property.
   * @enum {value} increase Increase in the observed value.
   * @enum {value} decrease Decrease in the observed value.
   * @enum {value} neutral No variation in the observed value.
   * @enum {value} missing No data is available.
   */
  @property()
  trend?: 'increase' | 'decrease' | 'neutral' | 'missing';

  /**
   * The polarity of the trend line.
   * @enum {value} positive The observed value has positive impact.
   * @enum {value} negative The observed value has negative impact.
   * @enum {value} neutral The observed value has no impact.
   */
  @property({ attribute: 'trend-polarity' })
  trendPolarity?: 'positive' | 'negative' | 'neutral' = 'neutral';

  /** Secondary details about the KPI to be displayed. */
  @property({ attribute: 'secondary-label' })
  secondaryLabel?: string;

  /** Display a single control (icon button) to view additional info */
  @property({ attribute: 'show-control-icon' })
  showControlIcon?: boolean;

  /** Fires when control icon is clicked. */
  @event('click', { cancelable: true, bubbles: true })
  private _click: EventEmitter;

  _handleClick() {
    this._click.emit();
  }

  renderControlIcon() {
    return html`
      ${!this.showControlIcon
        ? nothing
        : html`
            <div class="control">
              <button
                type="button"
                class="control-icon"
                @click=${this._handleClick}
              >
                <kat-icon
                  name="keyboard_control"
                  size="small"
                  part="kpi-bar-item-control-icon"
                ></kat-icon>
              </button>
            </div>
          `}
    `;
  }

  renderTrend() {
    const iconMapping = {
      increase: 'trending_up',
      decrease: 'trending_down',
      neutral: 'trending_neutral',
    };
    return html`
      ${!this.trend
        ? nothing
        : html`
            <div class="trend">
              <kat-icon
                class="${this.trendPolarity}"
                name="${iconMapping[this.trend]}"
                size="small"
                part="kpi-bar-item-trend-icon"
              ></kat-icon>
            </div>
          `}
    `;
  }

  renderSecondaryLabel() {
    return html`
      ${!this.secondaryLabel
        ? nothing
        : html`<div class="secondary-label">${this.secondaryLabel}</div>`}
    `;
  }

  render() {
    return html`
      <div class="wrapper">
        <div class="header">
          <span class="title" part="kpi-bar-item-label">${this.label}</span>
          ${this.renderControlIcon()}
        </div>
        <div class="metric-container">
          <div class="primary-metric">
            <div class="metric" part="kpi-bar-item-metric">${this.metric}</div>
            ${this.renderTrend()}
          </div>
          ${this.renderSecondaryLabel()}
        </div>
        <div class="visualization">
          <slot></slot>
        </div>
        <div class="error">
          <slot name="error"></slot>
        </div>
      </div>
    `;
  }
}
